/**
 * What the keyboard and MIDI input should play, derived from the timeline
 * selection.
 *
 * Pure and React-free so every routing case can be tested directly: selecting
 * the wrong target is silent-but-wrong, which is the hardest kind of bug to
 * notice while playing.
 */

export const MIDI_TARGET_NONE = 'none';
export const MIDI_TARGET_INSTRUMENT = 'instrument';
export const MIDI_TARGET_DRUM = 'drum';

const NONE = Object.freeze({ kind: MIDI_TARGET_NONE });

/** True when a clip is a drum pattern rather than an instrument pattern. */
export function isDrumPatternClip(clip) {
  return clip?.clipType === 'pattern' && clip?.clipData?.kind !== 'instrument';
}

/**
 * Resolve the live input target.
 *
 * - An instrument track routes to that track's own engine, so selecting
 *   Instrument 2 plays Instrument 2's patch rather than whichever engine the
 *   instrument panel happened to be showing.
 * - A track carrying drum patterns routes to one drum lane. The lane is
 *   whatever the sequencer last focused, defaulting to the top lane, and it is
 *   clamped so a deleted lane cannot leave input pointing at nothing.
 * - Audio tracks, unknown tracks and an empty selection route nowhere: audio
 *   clips have no notes to play.
 */
export function resolveMidiTarget({
  selectedTrackId = null,
  tracks = [],
  clips = [],
  drumSounds = [],
  drumIndex = null
} = {}) {
  if (!selectedTrackId) return NONE;
  const track = (Array.isArray(tracks) ? tracks : []).find((entry) => entry?.id === selectedTrackId);

  if (track?.type === 'instrument') {
    return { kind: MIDI_TARGET_INSTRUMENT, trackId: track.id };
  }

  // A drum lane is identified by the clips it holds, not by a tracks entry:
  // pattern lanes are not always present in the track list, and requiring one
  // there silently dropped drum routing back to the instrument.
  const hasDrumPattern = (Array.isArray(clips) ? clips : [])
    .some((clip) => clip?.trackId === selectedTrackId && isDrumPatternClip(clip));
  const virtualLane = /^drum-lane-(\d+)$/.exec(String(selectedTrackId));
  if (!hasDrumPattern && !virtualLane) return NONE;

  const lanes = Array.isArray(drumSounds) ? drumSounds.filter(Boolean) : [];
  if (lanes.length === 0) return NONE;
  const requested = virtualLane
    ? Number(virtualLane[1])
    : (Number.isFinite(Number(drumIndex)) ? Math.trunc(Number(drumIndex)) : 0);
  const index = Math.max(0, Math.min(lanes.length - 1, requested));
  const lane = lanes[index];
  if (!lane?.key) return NONE;
  return { kind: MIDI_TARGET_DRUM, drumIndex: index, drumKey: lane.key, drumName: lane.name || lane.key };
}

/** Convenience predicate for callers that only need to know if input is live. */
export function isPlayableTarget(target) {
  return Boolean(target) && target.kind !== MIDI_TARGET_NONE;
}
